import { Button, Image } from "@heroui/react";
import Tree from "react-d3-tree";
import { useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";

import sge2 from "../../../public/SGE2.png";

const PedigreeProfile = () => {
  const { id } = useParams();
  const contentRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({ contentRef });
  const [gn, setGn] = useState<string | undefined>();
  const [animal, setAnimal] = useState<any | null>(null);

  useEffect(() => {
    setGn(id);
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (gn) {
          const response = await fetch(
            `https://app-slc7wyklya-uc.a.run.app/v1/api/animals/locate/${gn}`,
            {
              method: "GET",
            },
          );

          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          const animal = await response.json();

          setAnimal(animal.animal);
        }
      } catch (error) {
        console.error("Error fetching animal.animal:", error);
      }
    };

    if (gn)
      fetchData().catch((error) =>
        console.error("Fetch" + " animal.animal" + " error:", error),
      );
  }, [gn]);

  if (!animal) {
    return <div>No Animal</div>;
  }

  const orgChart = {
    name: "Animal",
    children: [
      {
        name: "Profile",
        attributes: {
          Name: animal.name || "unknown",
          Breed: animal.racialComposition || "unknown",
          DGR: animal.DGR || "unknown",
        },
        children: [
          {
            name: "Dam",
            attributes: {
              Breed: animal.damName || "unknown",
              DGR: animal.damName || "unknown",
            },
            children: [
              {
                name: "Maternal GrandDam",
                attributes: {
                  Breed: animal.maternalGrandDamBreed || "unknown",
                  DGR: animal.maternalGrandDamBreed || "unknown",
                },
              },
              {
                name: "Maternal GrandSire",
                attributes: {
                  Breed: animal.maternalGrandSireBreed || "unknown",
                  DGR: animal.maternalGrandSireDGR || "unknown",
                },
              },
            ],
          },
          {
            name: "Sire",
            attributes: {
              Breed: animal.sireBreed || "unknown",
              DGR: animal.sireDGR || "unknown",
            },
            children: [
              {
                name: "Paternal GrandDam",
                attributes: {
                  Breed: animal.paternalGrandDamBreed || "unknown",
                  DGR: animal.paternalGrandDamDGR || "unknown",
                },
              },
              {
                name: "Paternal GrandSire",
                attributes: {
                  Breed: animal.paternalGrandSireBreed || "unknown",
                  DGR: animal.paternalGrandSireDGR || "unknown",
                },
              },
            ],
          },
        ],
      },
    ],
  };

  return (
    <div className={"px-10 h-full w-full overflow-auto "}>
      {animal ? (
        <>
          <div
            ref={contentRef}
            className={
              "print-landscape relative" +
              " h-full w-full " +
              " rounded-md" +
              ` border-cyan-500 bg-[url("/frame.png")]` +
              " bg-blend-screen bg-no-repeat bg-contain"
            }
          >
            <div className={"overflow-x-auto overflow-hidden "}>
              <div className={"  rounded-md"}>
                <div className={"  rounded-md"}>
                  <div
                    className={
                      "relative top-20 overflow-x-auto" +
                      " overflow-y-auto" +
                      " text-gray-700" +
                      ' font-["suse"] text-2xl' +
                      " h-full w-full flex flex-col items-center" +
                      " justify-between gap-10" +
                      " "
                    }
                  >
                    <div
                      className={
                        "relative " +
                        "  flex" +
                        " flex-col" +
                        " items-center" +
                        " justify-center"
                      }
                    >
                      <Image
                        isBlurred
                        alt="NextUI Album Cover"
                        sizes={"auto"}
                        src={sge2}
                      />
                      <span
                        className={
                          "text-xl lg:text-3xl py-2 " + 'font-["Pinyon Script"]'
                        }
                      >
                        SILA GREEN GENETICS ANIMAL CERTIFICATE
                      </span>
                      <span className={"text-xs lg:text-sm"} />
                    </div>
                    <div
                      className={
                        "w-full relative bottom-10 px-10 lg:px-0 " +
                        " flex" +
                        " flex-row" +
                        " justify-evenly text-xs lg:text-medium"
                      }
                    >
                      <div className={"flex flex-col "}>
                        <span>
                          Name: {animal.name ? animal.name : "unknown"}
                        </span>
                        <span>DGR: {animal.DGR ? animal.DGR : "unknown"}</span>
                        <span>
                          Breed:{" "}
                          {animal.racialComposition
                            ? animal.racialComposition
                            : "unknown"}
                        </span>
                        <span>Sex: {animal.sex ? animal.sex : "unknown"}</span>
                      </div>
                      <div className={"flex flex-col relative "}>
                        <span>
                          Sire DGR:{" "}
                          {animal.sireDGR ? animal.sireDGR : "unknown"}
                        </span>
                        <span>
                          Sire Breed:{" "}
                          {animal.sireBreed ? animal.sireBreed : "unknown"}
                        </span>
                        <span>
                          Sire Name:{" "}
                          {animal.sireName ? animal.sireName : "unknown"}
                        </span>
                      </div>
                      <div className={"flex flex-col relative "}>
                        <span>
                          Dam DGR: {animal.damDGR ? animal.damDGR : "unknown"}
                        </span>
                        <span>
                          Dam Name:{" "}
                          {animal.damName ? animal.damName : "unknown"}
                        </span>
                        <span>
                          Dam Breed:{" "}
                          {animal.damBreed ? animal.damBreed : "unknown"}
                        </span>
                      </div>
                    </div>
                    <div
                      className={
                        "relative bottom-12 h-[280px] w-[380px]" + " rounded-lg"
                      }
                    >
                      <Tree
                        collapsible={false}
                        data={orgChart && orgChart}
                        draggable={true}
                        orientation={"horizontal"}
                        pathFunc={"elbow"}
                        zoom={0.4}
                        zoomable={false}
                      />
                    </div>
                    <div
                      className={
                        "w-fit flex flex-row relative" +
                        " -top-56" +
                        " justify-between gap-96" +
                        " items-center"
                      }
                    >
                      <span>
                        <h3
                          className={
                            "text-gray-700 border-t-gray-500 border-t-1" +
                            " text-sm lg:text-xl"
                          }
                        >
                          Manager Signature
                        </h3>
                      </span>
                      <Image
                        isBlurred
                        alt="NextUI Album Cover"
                        className={"w-20 h-12 lg:w-[200px] lg:h-[100px] "}
                        sizes={"auto"}
                        src={sge2}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Button
            fullWidth
            className={"relative bottom-4"}
            color={"primary"}
            onPress={() => reactToPrintFn()}
          >
            Print
          </Button>
        </>
      ) : (
        <div>No Animal Found</div>
      )}
    </div>
  );
};

export default PedigreeProfile;
